import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Activity, Thermometer, HeartPulse, Scale } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";

const VITALS = [
  { icon: HeartPulse, label: "Blood Pressure" },
  { icon: Activity, label: "SpO₂ & Heart Rate" },
  { icon: Thermometer, label: "Temperature" },
  { icon: Scale, label: "Height · Weight · BMI" },
];

const KioskShowcase = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [70, -70]);

  return (
    <section id="experience" ref={ref} data-testid="kiosk-showcase-section" className="relative py-24 sm:py-32 bg-charcoal/40 overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <div>
            <SectionHeading
              index="03"
              kicker="The Star of the Summit"
              title="The MedX AI Health & Wellness Kiosk."
            />
            <Reveal delay={0.15}>
              <p className="mt-6 text-base sm:text-lg leading-relaxed text-white/60">
                A self-service clinical station delivering <span className="text-brand-300 font-semibold">30+ vital
                screenings in under 3 minutes</span>. At The Choice, every delegate experiences a full live
                demonstration — presented in Detroit by MedX AI President Samantha “Sammy” Stephenson.
              </p>
            </Reveal>
            <div className="mt-10 grid grid-cols-2 gap-3">
              {VITALS.map((v, i) => (
                <Reveal key={v.label} delay={0.1 + i * 0.06}>
                  <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-obsidian px-4 py-3.5">
                    <v.icon className="h-4 w-4 shrink-0 text-brand-400" />
                    <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-white/70">{v.label}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="relative">
            <Reveal>
              <div className="orange-glow overflow-hidden rounded-2xl border border-brand-500/30">
                <img
                  src="/assets/kiosk-clinic.jpg"
                  alt="MedX AI Health & Wellness Kiosk in a clinical environment"
                  className="aspect-[4/3] w-full object-cover"
                />
              </div>
            </Reveal>
            <motion.div
              style={{ y }}
              className="absolute -bottom-12 -left-4 sm:-left-12 w-44 sm:w-64 overflow-hidden rounded-xl border border-white/15 shadow-2xl"
            >
              <img
                src="/assets/kiosk-lobby.jpg"
                alt="MedX AI kiosk deployed in a corporate lobby"
                className="aspect-[3/4] w-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default KioskShowcase;
